export default class BCMSRecordCRUD {
    _operations;
    _controller;
    
    // constructor :: BCMSController, [FUNCTION] -> BCMSRecordCRUD
    constructor(controller, operations) {
        this.controller = controller;
        this.operations = operations ?? [];
    }

    // operations :: [FUNCTION] -> VOID
    // setter for the operations variable
    set operations(value) {
        if(Array.isArray(value)) {
            if(value.every(item => item instanceof Function)) {
                this._operations = value;
            } else {
                throw new Error("Items of operations must be a FUNCTION");
            }
        } else {
            throw new Error("Operations of BCMSRecordCRUD must be an ARRAY");
        }
    }

    // controller :: OBJECT -> VOID
    // setter for the controller variable
    set controller(value) {
        this._controller = value;
    }

    // operations :: * -> [FUNCTION]
    // getter for the operations variable
    get operations() {
        return this._operations;
    }

    // controller :: * -> OBJECT
    // getter for the controller variable
    get controller() {
        return this._controller;
    }

    // validate :: STRING, STRING, STRING, OBJECT -> VOID
    // Check if the 'type' and 'prop' parameters exist within the passed 'state'
    // if either one does not exist, throw an error
    validate(type, prop, operation, state) {
        if(state[type] == undefined) { 
            throw new Error(`Failed to ${ operation } record: "${ type }" does not exist within state`); 
        }
        else if(Array.isArray(state[type]) && state[type].length > 0 && state[type][0][prop] == undefined) { 
            throw new Error(`Failed to ${ operation } record: "${ prop }" does not exist within "${ type }"`); 
        }
    }

    // update :: STRING, STRING, *, OBJECT -> this
    // Add the update function to the array of operations then return self
    update(type, prop, value, record) {
        this.operations.push(state => {
            this.validate(type, prop, "updated", state);

            if(Array.isArray(state[type])) {
                const item = state[type].find(item => item[prop] === value);
                state[type][state[type].indexOf(item)] = record;
            }
            else { state[type] = record; }

            return state;
        });
        
        return this;
    }

    // delete :: STRING, STRING, * -> this
    // Add the delete function to the array of operations then return self
    delete(type, prop, value) {
        this.operations.push((state) => {
            this.validate(type, prop, "delete", state);

            state[type] = state[type].filter(item => item[prop] !== value);
            return state;
        });

        return this;
    }

    // create :: STRING, OBJECT -> this
    // Add the create function to the array of operations then return self
    create(type, record) {
        this.operations.push((state) => {
            if(state[type] == undefined) {
                throw new Error(`Failed to create record: ${ type } does not exist within state`);
            }

            return { ...state, [type] : [ ...state[type], record ] };
        });

        return this;
    }

    // read :: STRING, STRING, * -> OBJECT|undefined
    // Return the item from the state based on the 'type' and 'prop' parameters
    // If the 'type' in the state is an array, get the item based on 'prop' and 'value' parameters.
    // If the record returned does not exist return undefined otherwise a cloned object
    read(type, prop, value) {
        const { state } = this.controller;
        const record = Array.isArray(state[type])
            ? state[type].find(item => item[prop] == value)
            : state[type];

        return record ? JSON.parse(JSON.stringify(record)) : undefined;
    }

    // submit :: * -> VOID
    // Reduce over the existing operations passing state to each operation, then dispatch an update
    // to model passing the updated state object

    // NOTE: The state that is returned by an operation is the state passed to the next operation in the list
    // operations take place in the order in which they were added
    submit(classes) {
        const { state, dispatcher } = this.controller;
        const updatedState = this.operations.reduce((result, operation) => operation(result), state);

        dispatcher("updateModel", updatedState, classes);
    }

    // init :: [FUNCTION]
    // Return a new instance of BCMSRecordCRUD
    static init(operations) {
        return new BCMSRecordCRUD(operations);
    }

    // batch :: [BCMSRecordCRUD], BCMSController -> VOID
    // Reduce over all instances and return an array of all operations, then reduce over all operations
    // returning a modified state. Then dispatch an update to model passing the modified state object
    static batch(instances, controller, classes) {
        const { state, dispatcher } = controller;
        const operations = instances.reduce((result, instance) => result.concat(instance.operations), []);
        const updatedState = operations.reduce((result, operation) => operation(result), state);

        dispatcher("updateModel", updatedState, classes);
    }
}